import { Component, OnInit } from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {UomModel} from '../../../../DataModels/uom-model';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';

@Component({
  selector: 'app-uom-form',
  templateUrl: './uom-form.component.html',
  styleUrls: ['./uom-form.component.css'],
  standalone: false
})
export class UomFormComponent implements OnInit {

  uomID;
  private uom: UomModel;
  uomForm: UntypedFormGroup;
  submitted = false;
  isSaveNew = false;
  originalSnapshot: any = {};

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(private table: DataService,
              private notification: BsToastService,
              private formBuilder: UntypedFormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private confirmDialogService: ConfirmDialogService,
              private titleService: Title,
              private nav: NavigationService) {
    this.createForm();
    this.uomID = this.route.snapshot.params.id;
  }

  get f(){
    return this.uomForm.controls;
  }

  createForm() {
    this.uomForm = this.formBuilder.group({
      name: new UntypedFormControl(''),
      code: new UntypedFormControl(''),
      plural: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
      metric: new UntypedFormControl(true)
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:UOM@@uom:UOM`);
    if (this.uomID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData() {
    this.table.getData('uom', this.uomID).subscribe(
      (result: any) => {
        const data = (result && result.Data !== undefined) ? result.Data : result;
        this.uom = data as UomModel;
        this.uomForm.patchValue(this.uom);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      },
      (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Location Access @@errorloadingLocationAccess:Error loading Location Access`);
        console.log(error);
      }
    );
  }

  saveClose() {
    if (this.onSave(true)) {
      this.onClose();
    }
  }

  saveNew() {
    // Clear only on Save & New and only after async save succeeds
    this.isSaveNew = true;
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  private resetToDefaults(): void {
    // Reset the form to known defaults and clear component state for a fresh entry
    this.uomForm.reset({
      name: '',
      code: '',
      plural: '',
      active: true,
      metric: true
    });
    this.uom = {name: '', code: '', plural: '', active: true, metric: true} as unknown as UomModel;
    this.uomID = null;
    this.submitted = false;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    const fallback = '/admin/uom-list';
    this.nav.backOr(fallback);
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [$localize`:Are you sure you want to delete this UOM? @@areYouSureYouWantToDeleteThisUOM:Are you sure you want to delete this UOM?`, this.uom.name];
    this.confirmDialogService.confirmThis(messages,
      () => {
        this.table.deleteDataById('uom', this.uomID).subscribe(
          result => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          });
      }, () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      });
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    if (this.uomForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return false;
    }
    this.uom = Object.assign({}, this.uomForm.value);
    if (this.uomID) {
      if (this.uomForm.invalid === false) {
        this.table.patch('uom', this.uomID, this.uom).then(
          () => {
            try {
              this.originalSnapshot = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            } else if (this.isSaveNew) {
              this.isSaveNew = false;
              this.resetToDefaults();
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('uom', this.uom).then(res => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.uomID = apiReturn.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }
    return true;
  }

  buildSnapshot(): any {
    try {
      return this.uomForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
